import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { Tooltip, IconButton } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";

import styles from "../masters/UserManagement.module.css";
import CustomMRTTable from "../../components/CustomMRTTable/CustomMRTTable";
import AxiosInstance from "../../services/Api";

const extractList = (response) => {
  const data = response?.data;
  if (Array.isArray(data?.app_version)) return data.app_version;
  if (Array.isArray(data?.appVersion)) return data.appVersion;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.list)) return data.list;
  if (Array.isArray(data)) return data;
  return [];
};

const isSuccess = (response) =>
  response?.data?.status === true || response?.data?.success === true;

const formatListDate = (value) => {
  if (!value) return "—";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "—";
};

const getOsType = (row) =>
  row.os_type ||
  (row.android_version != null && row.android_version !== ""
    ? "Android"
    : row.ios_version
      ? "iOS"
      : "—");

/** Prefer unified version; legacy split columns only if version missing */
const getVersion = (row) => {
  if (row.version != null && String(row.version).trim() !== "") {
    return String(row.version).trim();
  }
  if (row.android_version != null && row.android_version !== "") {
    return row.android_version;
  }
  return row.ios_version || "—";
};

const getStatusLabel = (row) => {
  const raw = row.status ?? row.version_status;
  if (raw === 1 || raw === "1" || String(raw).toLowerCase() === "active") {
    return "Active";
  }
  if (raw === 0 || raw === "0" || String(raw).toLowerCase() === "inactive") {
    return "Inactive";
  }
  return "Inactive";
};

const AppVersionList = () => {
  const navigate = useNavigate();

  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const fetchVersions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await AxiosInstance.post("/app-version/list", {});
      if (isSuccess(response)) {
        setVersions(extractList(response));
      } else {
        setVersions([]);
        throw new Error(
          response?.data?.message || "Failed to fetch app versions",
        );
      }
    } catch (err) {
      console.error("Error fetching app versions:", err);
      setError(
        err?.response?.data?.message || err?.message || "An error occurred",
      );
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  const handleView = useCallback(
    (row) => {
      if (!row?.id) return;
      navigate(`/dashboard/appversion/view/${row.id}`);
    },
    [navigate],
  );

  const handleEdit = useCallback(
    (row) => {
      if (!row?.id) return;
      navigate(`/dashboard/appversion/edit/${row.id}`);
    },
    [navigate],
  );

  const columns = useMemo(
    () => [
      {
        id: "serial",
        header: "S.No",
        size: 70,
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnActions: false,
        Cell: ({ row }) =>
          pagination.pageIndex * pagination.pageSize + row.index + 1,
      },
      {
        accessorKey: "os_type",
        header: "Platform",
        size: 120,
        Cell: ({ row }) => getOsType(row.original),
      },
      {
        accessorKey: "version",
        header: "Version",
        size: 120,
        Cell: ({ row }) => getVersion(row.original),
      },
      {
        accessorKey: "build_type",
        header: "Build Type",
        size: 130,
        Cell: ({ row }) =>
          row.original.build_type || row.original.type || "—",
      },
      {
        accessorKey: "submitted_date",
        header: "Submitted Date",
        size: 150,
        Cell: ({ row }) =>
          formatListDate(
            row.original.submitted_date || row.original.created_date,
          ),
      },
      {
        accessorKey: "released_date",
        header: "Released Date",
        size: 150,
        Cell: ({ cell }) => formatListDate(cell.getValue()),
      },
      {
        id: "status",
        header: "Status",
        size: 120,
        Cell: ({ row }) => {
          const label = getStatusLabel(row.original);
          const isActive = label === "Active";
          return (
            <span
              style={{
                display: "inline-block",
                padding: "4px 12px",
                borderRadius: "999px",
                fontSize: "12px",
                fontWeight: 600,
                background: isActive ? "#E8F5E9" : "#FFEBEE",
                color: isActive ? "#2E7D32" : "#C62828",
                border: `1px solid ${isActive ? "#A5D6A7" : "#EF9A9A"}`,
              }}
            >
              {label}
            </span>
          );
        },
      },
      {
        accessorKey: "force_update",
        header: "Force Update",
        size: 140,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          const enabled = value === true || value === 1 || value === "1";
          return (
            <span
              style={{
                display: "inline-block",
                padding: "4px 12px",
                borderRadius: "999px",
                fontSize: "12px",
                fontWeight: 600,
                background: enabled ? "#E8F5E9" : "#FFF3E0",
                color: enabled ? "#2E7D32" : "#E65100",
                border: `1px solid ${enabled ? "#A5D6A7" : "#FFCC80"}`,
              }}
            >
              {enabled ? "Yes" : "No"}
            </span>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        size: 120,
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnActions: false,
        Cell: ({ row }) => (
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <Tooltip title="View" arrow>
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleView(row.original);
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit" arrow>
              <IconButton
                size="small"
                color="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(row.original);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
    ],
    [pagination.pageIndex, pagination.pageSize, handleView, handleEdit],
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>APP VERSION</h1>
        <div
          className={styles.headerActions}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "nowrap",
          }}
        >
          <button
            type="button"
            className={styles.menuButton}
            onClick={() => navigate("/dashboard/appversion/create")}
            aria-label="Add App Version"
            title="Add App Version"
            style={{
              width: "auto",
              padding: "0 10px",
              gap: "6px",
              whiteSpace: "nowrap",
            }}
          >
            <i className="fa-solid fa-plus" />
            <span style={{ fontSize: "0.8125rem", fontWeight: 600 }}>Add</span>
          </button>
        </div>
      </div>

      {error ? (
        <div className={styles.errorMessage}>
          {error}
          <button type="button" onClick={fetchVersions} className={styles.retryButton}>
            Retry
          </button>
        </div>
      ) : null}

      <div className={styles.tableSection}>
        <CustomMRTTable
          columns={columns}
          data={versions}
          loading={loading}
          manualPagination={false}
          manualFiltering={false}
          pagination={pagination}
          onPaginationChange={setPagination}
          enablePagination
          enableSorting
          enableSearch={false}
          enableColumnVisibility
          enableDensityToggle
          enableFullScreenToggle
          enableHiding
          enableStickyHeader
        />
      </div>
    </div>
  );
};

export default AppVersionList;
